import React, { useState } from 'react';
import { Input, Modal } from 'antd';
import styled from 'styled-components';
import { ALIGNMENT_STATUS } from '../../services/communications/communicationsTypes.js';

const Strip = styled.div`
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
  background: ${(p) => (p.$warn ? '#fffbeb' : '#ecfdf5')};
  border: 1px solid ${(p) => (p.$warn ? '#fcd34d' : '#6ee7b7')};
`;

const StripRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StripText = styled.div`
  flex: 1;
  min-width: 200px;
  line-height: 1.45;
  color: #333;
`;

const Positions = styled.div`
  font-size: 0.78rem;
  color: #666;
  margin-top: 0.25rem;
`;

const Answer = styled.p`
  margin: 0.75rem 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 0.82rem;
`;

export default function AlignmentStrip({
  conflict,
  onAsk,
  onRecordDecision,
  alignmentAnswer,
  alignmentLoading,
}) {
  const [question, setQuestion] = useState('');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionText, setDecisionText] = useState('');

  if (!conflict) return null;

  const isConflict =
    conflict.status === ALIGNMENT_STATUS.CONFLICT ||
    conflict.status === ALIGNMENT_STATUS.PARTIAL;

  if (!isConflict && conflict.status === ALIGNMENT_STATUS.ALIGNED) {
    return (
      <Strip>
        All platforms aligned on <strong>{conflict.threadTopic}</strong>
      </Strip>
    );
  }

  if (!isConflict) return null;

  return (
    <Strip $warn>
      <StripText>
        <strong>Platforms may not agree</strong> on {conflict.threadTopic}
        <Positions>
          {conflict.bySource?.outlook?.decisionPosition ? `Outlook: ${conflict.bySource.outlook.decisionPosition}` : null}
          {conflict.bySource?.teams?.decisionPosition ? ` · Teams: ${conflict.bySource.teams.decisionPosition}` : null}
          {conflict.bySource?.planner?.decisionPosition ? ` · Planner: ${conflict.bySource.planner.decisionPosition}` : null}
        </Positions>
      </StripText>
      <StripRow>
        <Input
          size="small"
          placeholder="Ask about this discrepancy…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onPressEnter={() => onAsk?.(question)}
          style={{ width: 220, fontFamily: 'Poppins, sans-serif' }}
        />
        <button
          type="button"
          className="btn-primary btn-primary--small"
          disabled={alignmentLoading || !question.trim()}
          onClick={() => onAsk?.(question)}
        >
          Ask
        </button>
        <button
          type="button"
          className="btn-secondary btn-secondary--small"
          onClick={() => setDecisionOpen(true)}
        >
          Record decision
        </button>
      </StripRow>
      {alignmentAnswer ? <Answer>{alignmentAnswer.summary}</Answer> : null}

      <Modal
        title="Record aligned decision"
        open={decisionOpen}
        onOk={async () => {
          await onRecordDecision?.(decisionText);
          setDecisionOpen(false);
          setDecisionText('');
        }}
        onCancel={() => setDecisionOpen(false)}
        okButtonProps={{ disabled: !decisionText.trim() }}
      >
        <Input.TextArea
          rows={3}
          value={decisionText}
          onChange={(e) => setDecisionText(e.target.value)}
          placeholder="Enter the decision the team agrees on"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        />
      </Modal>
    </Strip>
  );
}
